import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Tooltip } from '@creditornot/cb-popover';
import React, { forwardRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { MetricRow } from 'modules/insights-v2/types';

import Trend from '../Trend';

const Metrics = styled(T.Heading6)`
  color: ${cbSemanticCssVars.textPrimary};
  text-align: end;
`;

const Root = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
`;

const Data = styled.div`
  align-items: baseline;
  display: flex;
  gap: 4px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Subtitle = styled(T.Small)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const Title = forwardRef<HTMLSpanElement, Omit<React.HTMLProps<HTMLSpanElement>, 'ref' | 'as'>>(
  ({ id, ...rest }, ref) => (
    <T.Body2 {...rest} ref={ref}>
      <FormattedMessage id={id} />
    </T.Body2>
  ),
);

const TitleWithTooltip = ({ title, tooltip }: { title: string; tooltip?: string }) => {
  const { formatMessage } = useIntl();

  if (!tooltip) {
    return <Title id={title} />;
  }

  return (
    <Tooltip content={formatMessage({ id: tooltip })}>
      <Title id={title} />
    </Tooltip>
  );
};

const InsightMetricRow = ({ data }: { data: MetricRow }) => {
  return (
    <Root>
      <TitleContainer>
        <TitleWithTooltip title={data.title} tooltip={data.titleTooltip} />
        <Subtitle>{data.subtitle}</Subtitle>
      </TitleContainer>
      <Data>
        {data.growthRate && data.growthTrend && <Trend trend={data.growthTrend}>{data.growthRate}</Trend>}
        <Metrics>{data.metrics}</Metrics>
      </Data>
    </Root>
  );
};

export default InsightMetricRow;
